import type { IRecordAcceptance, IRequiredAcceptanceResponse, TAcceptanceScope } from '@escloud/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userKeys } from './use-current-user';

import { useApi, useTenant } from '@/context';
import { DEFAULT_REGION } from '@/lib/region-config';

/**
 * Acceptances are recorded per region, against that region's own user row, so
 * the region is part of the cache identity. Sharing one entry across regions
 * would let a cached "nothing to accept" suppress another region's EULA gate
 * (ESCLOUD-311). The scope is part of it too (ESCLOUD-287): the EULA gate and
 * the device-use gate ask different questions, and a cached answer to one must
 * never stand in for the other.
 */
export const legalKeys = {
    all: ['legal'] as const,
    requiredAcceptancesAll: () => [...legalKeys.all, 'required-acceptances'] as const,
    requiredAcceptances: (region: string, scope: TAcceptanceScope = 'eula') =>
        [...legalKeys.requiredAcceptancesAll(), region, scope] as const,
    myAcceptances: (region: string) => [...legalKeys.all, 'acceptances', 'me', region] as const,
};

/**
 * Gate query (ESCLOUD-79 / ESCLOUD-287): document versions the current user
 * must (re)accept. Defaults to the EULA scope, so existing callers keep their
 * answer; the device-use gate passes `scope: 'device_use'`. `region` targets a
 * specific regional API: the funnel has no tenant in context yet, so it names
 * the intent's region explicitly (ESCLOUD-332).
 */
export function useRequiredAcceptances(
    options: { enabled?: boolean; scope?: TAcceptanceScope; region?: string } = {}
) {
    const { legal } = useApi();
    const { tenantRegion } = useTenant();
    const scope = options.scope ?? 'eula';
    const region = options.region ?? tenantRegion ?? DEFAULT_REGION;
    return useQuery({
        queryKey: legalKeys.requiredAcceptances(region, scope),
        queryFn: () => legal.getRequiredAcceptances(scope, options.region),
        // The gate blocks the whole authenticated UI — avoid refetch churn.
        staleTime: 5 * 60 * 1000,
        enabled: options.enabled ?? true,
    });
}

/**
 * Records one acceptance decision, then refreshes the gate state. `region`
 * targets a specific regional API, same reasoning as `useRequiredAcceptances`.
 */
export function useRecordAcceptance(options: { region?: string } = {}) {
    const { legal } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: IRecordAcceptance) => legal.recordAcceptance(data, options.region),
        onSuccess: (_response, accepted) => {
            // Clear the gate entry from every cached scope directly: the
            // acceptance is durably recorded server-side at this point, so the
            // blocking modal must close even if the confirmation refetch below
            // fails. `setQueriesData` covers the per-scope entries (eula,
            // device_use, all) in one pass.
            queryClient.setQueriesData<IRequiredAcceptanceResponse[]>(
                { queryKey: legalKeys.requiredAcceptancesAll() },
                (required) => required?.filter((doc) => doc.documentType !== accepted.documentType)
            );
            void queryClient.invalidateQueries({ queryKey: legalKeys.all });
            // The onboarding-state answer carries its own copy of what is owed,
            // so it goes stale on this write too. Re-asked rather than patched:
            // the server is the one deciding what is still outstanding.
            void queryClient.invalidateQueries({ queryKey: userKeys.onboardingStateAll() });
        },
    });
}

/** The current user's acceptance history (Past EULA versions dialog). */
export function useMyAcceptances(options: { enabled?: boolean } = {}) {
    const { legal } = useApi();
    const { tenantRegion } = useTenant();
    return useQuery({
        queryKey: legalKeys.myAcceptances(tenantRegion ?? DEFAULT_REGION),
        queryFn: () => legal.getMyAcceptances(),
        enabled: options.enabled ?? true,
    });
}
